import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as Editor from '../../../assets/ckeditor/build/ckeditor';
import { AdminService } from 'src/app/shared/services/admin.service';

interface Subject {
  subject_id: string;
  name: string;
  systems: System[];
  course_id: number;
}

interface System {
  system_id: string;
  name: string;
  hours: number;
  minutes: number;
  videoLink: string;
  content: string;
}

@Component({
  selector: 'app-manage-course-schedule',
  templateUrl: './manage-course-schedule.component.html',
  styleUrls: ['./manage-course-schedule.component.scss']
})
export class ManageCourseScheduleComponent implements OnInit {
  public Editor = Editor;
  editorConfig = {
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'link',
      'bulletedList',
      'numberedList',
      '|',
      'outdent',
      'indent',
      '|',
      'blockQuote',
      'insertTable',
      'undo',
      'redo'
    ]
  };

  subjectForm: FormGroup;
  systemForm: FormGroup;
  subjects: Subject[] = [];
  selectedSubject: Subject | null = null;
  editingSystem: System | null = null;
  enableEdit = false;
  loader = false;

  // Dialog properties
  showSubjectDialog = false;
  showDeleteSubjectDialog = false;
  showSystemDialog = false;
  showDeleteSystemDialog = false;
  subjectDialogHeader = 'Add System';
  systemDialogHeader = 'Add Subject';
  subjectToDelete: Subject | null = null;
  systemToDelete: System | null = null;

  courses: any = [
    {
      "courseID": 115014, 
      "courseName": "USMLE Step 1"
    },
    {
      "courseID": 115015, 
      "courseName": "USMLE Step 2 CK"
    },
    {
      "courseID": 135631, 
      "courseName": "USMLE Step 3"
    }
  ]
  selectedCourse = 115014;

  constructor(private fb: FormBuilder, private adminService: AdminService) {
    this.subjectForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]]
    });

    this.systemForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      hours: [0, [Validators.required, Validators.min(0)]],
      minutes: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
      videoLink: ['', [Validators.required]],
      content: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    // Load existing subjects and systems from your backend service
    this.loadSubjects(false);
  }

  async changeCourses() {
    this.loadSubjects(false);
  }

  loadSubjects(value: boolean): void {
    this.loader = true;
    this.adminService.getCourseSchedule(this.selectedCourse).subscribe((res: any) => {
      this.loader = false;
      if (res.data.status) {
        this.subjects = res.data.courseSchedule;
        if (this.subjects.length > 0) {
          if (value) {
            this.selectedSubject = this.subjects[this.subjects.length - 1];
          } else {
            this.selectedSubject = this.subjects[0];
          }
        }
      }
    });
  }

  onSubmitSubject(): void {
    if (this.subjectForm.valid) {
      const newSubject: Subject = {
        subject_id: Date.now().toString(), // Temporary ID generation
        name: this.subjectForm.value.name,
        systems: [],
        course_id: this.selectedCourse
      };

      this.loader = true;
      this.adminService.addScheduleSubject(newSubject).subscribe((res: any) => {
        this.loader = false;
        if (res.data.status) {
          this.subjectForm.reset();
          this.loadSubjects(true);
        }
      });
    }
  }

  onSubmitSystem(): void {
    if (this.systemForm.valid && this.selectedSubject) {
      const newSystem: System = {
        system_id: Date.now().toString(), // Temporary ID generation
        ...this.systemForm.value,
        subjectId: this.selectedSubject.subject_id
      };

      if (this.editingSystem) {
        // Update existing system
        const systemIndex = this.selectedSubject.systems.findIndex(s => s.system_id === this.editingSystem?.system_id);
        if (systemIndex !== -1) {
          this.selectedSubject.systems[systemIndex] = newSystem;
        }
        this.editingSystem = null;
      } else {
        // Add new system
        this.selectedSubject.systems.push(newSystem);
      }

      this.loader = true;
      this.adminService.addScheduleSystem(newSystem).subscribe((res: any) => {
        this.loader = false;
        if (res.data.status) {
          this.systemForm.reset();
          this.loadSubjects(true);
        }
      });
    }
  }

  editSystem(system: System): void {
    this.systemDialogHeader = 'Edit Subject';
    this.systemForm.patchValue({
      name: system.name,
      hours: system.hours,
      minutes: system.minutes,
      videoLink: system.videoLink,
      content: system.content
    });
    this.editingSystem = system;
    this.showSystemDialog = true;
  }

  hideSystemDialog(): void {
    this.showSystemDialog = false;
    this.systemForm.reset();
    this.systemDialogHeader = 'Add Subject';
    this.editingSystem = null;
  }

  saveSystem(): void {
    if (this.systemForm.valid && this.editingSystem) {
      const updatedSystem = {
        system_id: this.editingSystem.system_id,
        ...this.systemForm.value
      };

      this.loader = true;
      this.adminService.updateScheduleSystem(updatedSystem).subscribe((res: any) => {
        this.loader = false;
        if (res.data.status) {
          this.hideSystemDialog();
          this.loadSubjects(false);
        }
      });
    }
  }

  deleteSystem(system: System): void {
    this.systemToDelete = system;
    this.showDeleteSystemDialog = true;
  }

  hideDeleteSystemDialog(): void {
    this.showDeleteSystemDialog = false;
    this.systemToDelete = null;
  }

  confirmDeleteSystem(): void {
    if (this.systemToDelete) {
      const data = {
        system_id: this.systemToDelete.system_id
      }
      this.loader = true;
      this.adminService.deleteScheduleSystem(data).subscribe((res: any) => {
        this.loader = false;
        if (res.data.status) {
          this.hideDeleteSystemDialog();
          this.loadSubjects(false);
        }
      });
    }
  }

  selectSubject(subject: Subject): void {
    this.selectedSubject = subject;
    this.systemForm.reset();
    this.editingSystem = null;
    this.enableEdit = false;
  }

  cancelSystem(): void {
    this.systemForm.reset();
    this.editingSystem = null;
    this.enableEdit = false;
    // Optionally, deselect subject if you want to hide the form
    // this.selectedSubject = null;
  }

  getTotalDuration(systems: System[]): string {
    let totalMinutes = systems.reduce((sum, sys) => sum + (sys.hours * 60 + sys.minutes), 0);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    let result = '';
    if (hours > 0) result += `${hours} Hours`;
    if (minutes > 0) result += (result ? ' & ' : '') + `${minutes} minutes`;
    return result || '0';
  }

  getSystemDuration(system: System): string {
    let result = '';
    if (system.hours > 0) result += `${system.hours} Hours`;
    if (system.minutes > 0) result += (result ? ' & ' : '') + `${system.minutes} minutes`;
    return result || '0';
  }

  getContentList(content: string): string[] {
    // Try to split by <li> if HTML, else by newlines
    if (!content) return [];
    const liMatches = content.match(/<li>(.*?)<\/li>/g);
    if (liMatches) {
      return liMatches.map(li => li.replace(/<li>|<\/li>/g, '').trim());
    }
    // Fallback: split by newlines or bullets
    return content.split(/\n|•/).map(item => item.replace(/<[^>]+>/g, '').trim()).filter(Boolean);
  }

  // Subject dialog methods
  editSubject(subject: Subject): void {
    this.subjectDialogHeader = 'Edit System';
    this.subjectForm.patchValue({
      name: subject.name
    });
    this.selectedSubject = subject;
    this.showSubjectDialog = true;
  }

  hideSubjectDialog(): void {
    this.showSubjectDialog = false;
    this.subjectForm.reset();
    this.subjectDialogHeader = 'Add System';
  }

  saveSubject(): void {
    if (this.subjectForm.valid && this.selectedSubject) {
      const updatedSubject = {
        subject_id: this.selectedSubject.subject_id,
        name: this.subjectForm.value.name
      };

      this.loader = true;
      this.adminService.updateScheduleSubject(updatedSubject).subscribe((res: any) => {
        this.loader = false;
        if (res.data.status) {
          this.hideSubjectDialog();
          this.loadSubjects(false);
        }
      });
    }
  }

  deleteSubject(subject: Subject): void {
    this.subjectToDelete = subject;
    this.showDeleteSubjectDialog = true;
  }

  hideDeleteSubjectDialog(): void {
    this.showDeleteSubjectDialog = false;
    this.subjectToDelete = null;
  }

  confirmDeleteSubject(): void {
    if (this.subjectToDelete) {
      const data = {
        subject_id: this.subjectToDelete.subject_id
      }
      this.loader = true;
      this.adminService.deleteScheduleSubject(data).subscribe((res: any) => {
        this.loader = false;
        if (res.data.status) {
          this.hideDeleteSubjectDialog();
          this.loadSubjects(false);
        }
      });
    }
  }
}
